import { c as _c } from "react/compiler-runtime";
import React, { useMemo, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect, SearchableSelectProps } from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { User } from '../../../classes/entities/User/userTypes';
import { UserUtils } from '../../../classes/entities/User/UserUtils';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
import { UseSelectEntityItemsUtils } from '../../../hooks/entity/useSelectEntityItems/UseSelectEntityItemsUtils';
export type UserSelectProps = PropsWithoutChildren<Omit<SearchableSelectProps<User>, 'getKeyOfItem' | 'getDisplayTextOfItem' | 'items' | 'value' | 'onValueChanged' | 'label'>> & {
  value: string | null;
  label?: string;
  onValueChanged: (value: string | null) => void;
  filterUsers?: (users: Array<User>) => Array<User>;
};
export const UserSelect: FunctionComponent<UserSelectProps> = React.memo(props => {
  const $ = _c(34);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let filterUsers;
  let label;
  let onValueChanged;
  let remainingProps;
  let value;
  if ($[2] !== t0) {
    ({
      value,
      label,
      onValueChanged,
      filterUsers,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = filterUsers;
    $[4] = label;
    $[5] = onValueChanged;
    $[6] = remainingProps;
    $[7] = value;
  } else {
    filterUsers = $[3];
    label = $[4];
    onValueChanged = $[5];
    remainingProps = $[6];
    value = $[7];
  }
  const {
    t
  } = useTranslation();
  const users = useEntities(EntityName.User, "getAll");
  let t1;
  if ($[8] !== filterUsers || $[9] !== users) {
    t1 = filterUsers ? filterUsers(users) : users;
    $[8] = filterUsers;
    $[9] = users;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const filteredUsers = t1;
  let t2;
  if ($[11] !== filteredUsers) {
    t2 = [null, ...filteredUsers];
    $[11] = filteredUsers;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const filteredUsersWithNullValue = t2;
  let t3;
  if ($[13] !== t) {
    t3 = t("components.input.UserSelect.userNotFound");
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t) {
    t4 = t("components.input.UserSelect.noUser");
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== filteredUsersWithNullValue || $[18] !== onValueChanged || $[19] !== t3 || $[20] !== t4 || $[21] !== value) {
    t5 = {
      value,
      entities: filteredUsersWithNullValue,
      entityNotFoundText: t3,
      nullValueText: t4,
      onValueChanged,
      getDisplayTextOfEntity: UserUtils.displayTextGetter,
      getKeyOfEntity: UserUtils.keyGetter,
      getValueOfEntity: UseSelectEntityItemsUtils.entityIdGetter
    };
    $[17] = filteredUsersWithNullValue;
    $[18] = onValueChanged;
    $[19] = t3;
    $[20] = t4;
    $[21] = value;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const useSelectEntityItemsReturnValue = useNullableSelectEntityItems(t5);
  let t6;
  if ($[23] !== label || $[24] !== t) {
    t6 = label != null ? label : t("components.input.UserSelect.label");
    $[23] = label;
    $[24] = t;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== remainingProps || $[27] !== t6 || $[28] !== useSelectEntityItemsReturnValue.getDisplayTextOfItem || $[29] !== useSelectEntityItemsReturnValue.getKeyOfItem || $[30] !== useSelectEntityItemsReturnValue.items || $[31] !== useSelectEntityItemsReturnValue.onSelectedItemChanged || $[32] !== useSelectEntityItemsReturnValue.selectedItem) {
    t7 = <SearchableSelect label={t6} items={useSelectEntityItemsReturnValue.items} value={useSelectEntityItemsReturnValue.selectedItem} onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged} getDisplayTextOfItem={useSelectEntityItemsReturnValue.getDisplayTextOfItem} getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem} data-testid="UserSelect.SearchableSelect" {...remainingProps} />;
    $[26] = remainingProps;
    $[27] = t6;
    $[28] = useSelectEntityItemsReturnValue.getDisplayTextOfItem;
    $[29] = useSelectEntityItemsReturnValue.getKeyOfItem;
    $[30] = useSelectEntityItemsReturnValue.items;
    $[31] = useSelectEntityItemsReturnValue.onSelectedItemChanged;
    $[32] = useSelectEntityItemsReturnValue.selectedItem;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
});